import type ar from './ar.ts';

const en: typeof ar = {
  common: {
    notSpecified: 'Not specified',
    urgent: 'Urgent',
    normal: 'Normal',
    name: 'Name',
    patientName: 'Patient name',
    service: 'Service',
    age: 'Age',
    gender: 'Gender',
    male: 'Male',
    female: 'Female',
    services: {
      standard: 'Doctor appointment',
      secondOpinion: 'Second opinion',
      homecare: 'Homecare',
    },
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    submit: 'Submit',
    loading: 'Loading...',
    searchPlaceholder: 'Search...',
    select: 'Select',
    all: 'All',
    previous: 'Previous',
    next: 'Next',
    close: 'Close',
    back: 'Back',
    delete: 'Delete',
    edit: 'Edit',
    yes: 'Yes',
    no: 'No',
    listSeparator: ', ',
  },
  home: {
    searchSpecialties: 'Search specialties...',
    quickOptions: 'Quick options',
    specialties: 'Specialties',
    searchResults: 'Search results',
    searchResultsFor: '{{value}} results for "{{query}}"',
    viewAll: 'View all',
    noSpecialties: 'No specialties',
    noSpecialtiesDescription: 'No specialties are available right now',
    noSpecialtiesMatching: 'No specialties matching "{{query}}"',
    consultationQuotaReached:
      'Consultation quota reached. Please contact the sales team.',
    printingQuotaReached:
      'Printing quota reached. Please contact the sales team.',
    sections: {
      upcomingAppointments: 'Upcoming appointments',
      noUpcomingAppointments: 'No upcoming appointments',
      secondOpinionRequests: 'Second opinion requests',
      noSecondOpinionRequests: 'No second opinion requests',
      homecareReservations: 'Homecare reservations',
      noHomecareReservations: 'No homecare reservations',
      printingRequests: 'Printing requests',
      noPrintingRequests: 'No printing requests yet',
    },
    options: {
      bookAppointment: {
        title: 'Book an appointment with a doctor',
        subtitle: 'Book your appointment to get a medical consultation',
        description:
          'Book an appointment with the best doctors in a range of specialties within minutes. Trusted medical consultations over video or text chat, whenever you need them, from anywhere.',
      },
      secondOpinion: {
        title: 'Get a second medical opinion',
        subtitle: 'Confirm your diagnosis with specialists',
        description:
          "Don't let doubt affect your health decisions. Book an appointment with experienced doctors for a trusted second opinion that helps you make the right decision about your treatment.",
      },
      homecare: {
        title: 'Homecare services',
        subtitle: 'Book a homecare service',
        description:
          'Get medical services at home from accredited providers. Nursing, physiotherapy, and a variety of health services delivered wherever you are.',
      },
      printing: {
        title: '3D Printing',
        subtitle: 'Request a 3D-printed medical model',
        description:
          'Submit a scan (zip) and our specialist team will produce an anatomical model, surgical guide, or prosthetic with our partner hospital.',
      },
    },
    dialog: {
      bookAppointment: 'Book an appointment with a doctor',
      secondOpinion: 'Get a second medical opinion',
    },
  },
  cards: {
    appointment: {
      prefix: 'Appointment',
      date: 'Appointment date',
      time: 'Appointment time',
      patientName: 'Patient name',
      doctorName: 'Doctor name',
    },
    secondOpinion: {
      prefix: 'Second opinion',
      specialty: 'Specialty',
      deliveryTime: 'Delivery time',
      priority: 'Priority',
      doctorName: 'Doctor name',
    },
    homecareReservation: {
      prefix: 'Homecare',
      service: 'Service',
      provider: 'Service provider',
      date: 'Appointment date',
      time: 'Appointment time',
    },
  },
  profile: {
    age: 'Age',
    entity: 'Organization',
  },
  pricing: {
    servicePrice: 'Service price',
    totalAmount: 'Total amount',
    insuranceDiscount: 'Insurance discount',
    vat: 'VAT',
    amountDue: 'Amount due',
    paymentDetailsTitle: 'Payment details',
    promoDiscount: 'Promo discount',
  },
  promoCode: {
    label: 'Promo code',
    placeholder: 'Enter promo code',
    apply: 'Apply',
    applying: 'Applying...',
    remove: 'Remove',
    appliedSummary: 'Applied {{code}} ({{percent}}% off)',
    havePromo: 'Have a promo code?',
    errors: {
      notFound: 'Invalid promo code',
      expired: 'Promo code has expired',
      notYetActive: 'Promo code is not yet active',
      limitReached: 'Promo code has reached its usage limit',
      generic: 'Could not apply promo code',
    },
  },
  status: {
    waitingConfirmation: 'Awaiting confirmation',
    waitingDoctorAssignment: 'Awaiting doctor assignment',
    waitingDoctorConfirmation: 'Awaiting doctor confirmation',
    finished: 'Completed',
    confirmed: 'Confirmed',
    canceled: 'Canceled',
    availableForFollowup: 'Available for follow-up',
    expired: 'Expired',
    unknown: 'Unknown status',
  },
  symptomForm: {
    subtitle:
      'The doctor will review this information when your appointment request is received',
    attachmentHint:
      'e.g. lab results, previous prescriptions, photos illustrating the condition if possible',
    privacyNote: 'Your medical information is confidential and protected',
    hints: {
      suffering: 'What are you experiencing?',
      chronic: 'Do you have any chronic illnesses?',
      onset: 'When did the symptoms start?',
    },
  },
  booking: {
    appointmentTitle: 'Book an appointment with a doctor',
    secondOpinionTitle: 'Get a second medical opinion',
    homecareTitle: 'Homecare services',
    specialtySelection: {
      title: 'Select a specialty',
      subtitle: 'Please select the specialty you need',
      searchPlaceholder: 'Search',
      noResults: 'No search results',
      back: 'Back',
      continue: 'Continue',
    },
    symptomChecker: {
      title: 'Describe symptoms',
      subtitleSelf:
        'Please fill in your details and attach any files if available',
      subtitleB2B:
        'Please fill in the patient details and attach any files if available',
      bookingForSelf: 'For myself',
      bookingForOther: 'For someone else',
      fields: {
        name: 'Name',
        age: 'Age',
        gender: 'Gender',
        symptoms: 'Symptom description',
        symptomsPlaceholder: 'I have a fever and a sore throat ...',
        patientName: 'Patient name',
        patientNamePlaceholder: 'Enter the patient name',
        doctorName: 'Referring doctor name',
        doctorNamePlaceholder: 'Enter the referring doctor name',
        guestEmail: 'Meeting attendee email',
        guestEmailPlaceholder: 'doctor@hospital.com',
        guestEmailHint:
          'The meeting link will be sent to this email automatically',
        ageSuffix: 'years',
      },
      validation: {
        symptomsMin:
          'Please describe the symptoms in detail (at least 10 characters)',
        patientName: 'Please enter the patient name',
        patientBirthDate: 'Please enter the date of birth',
        patientGender: 'Please select a gender',
        doctorName: 'Please enter the referring doctor name',
        guestEmail: 'Please enter the meeting attendee email',
        guestEmailInvalid: 'Please enter a valid email address',
      },
      submitting: 'Submitting...',
      back: 'Back',
      continue: 'Next: choose a doctor',
    },
    appointmentTime: {
      title: 'Book appointment',
      subtitle: 'Please choose a suitable time',
      back: 'Back',
      continue: 'Continue',
      pickDate: 'Pick a date',
      noSlots: 'No slots available on this day',
    },
    bookingConfirmation: {
      title: 'Confirm booking',
      subtitle: 'Please confirm the booking',
      detailsHeader: 'Booking details',
      appointmentPill: 'Appointment',
      back: 'Back',
      confirm: 'Confirm booking',
      payAndConfirm: 'Pay and confirm',
      processing: 'Processing...',
      servicePrice: 'Service price',
      amountDue: 'Amount due',
      missingReservation: "Couldn't find booking details. Please try again.",
      paymentFailed: "Couldn't complete the payment. Please try again.",
      missingDoctorName: 'Please enter the referring doctor name',
      missingGuestEmail: 'Please enter the meeting attendee email',
      remainingQuota: 'Remaining quota',
      consultationsLeft: 'Remaining consultations: {{remaining}}',
      topUpHint: 'Contact your administrator to top up.',
      submittedWithRemaining:
        'Booking confirmed. Remaining consultations: {{remaining}}.',
    },
    requestStatus: {
      paymentFailed: 'Payment failed',
      paymentFailedDescription:
        'The payment could not be completed. Please try again or contact support.',
      underReview: 'Request under review',
      underReviewDescription:
        'Your request is under review. We will notify you by email / SMS once processed and confirmed.',
      underReviewHomecareDescription:
        'Your homecare request is under review. We will notify you by email / SMS once processed and confirmed.',
      retry: 'Retry',
      home: 'Home',
      backHome: 'Back to home',
      viewAppointments: 'View appointments',
    },
    paymentDescription: 'Doctor appointment booking',
  },
  secondOpinion: {
    title: 'Second medical opinion',
    pageTitle: 'Second medical opinion',
    notFound: 'Request not found.',
    urgent: 'Urgent',
    normal: 'Normal',
    priority: 'Priority',
    urgentDescription:
      'Your request will be answered within 24 hours. Additional cost applies.',
    normalDescription: 'Your request will be answered within 72 hours.',
    selectPriority: 'Select priority',
    submitRequest: 'Submit request',
    urgentRequest: 'Urgent request',
    normalRequest: 'Standard request',
    card: {
      titleWithSpecialty: 'Second opinion — {{specialty}}',
      requestNumber: 'Request number:',
      priority: 'Priority:',
      dueDate: 'Due by:',
      serviceType: 'Service type:',
      caseDoctor: 'Assigned doctor:',
      statusLabel: 'Status:',
      priceLabel: 'Price:',
      paid: 'Paid',
      attachmentsHeader: 'Submitted information',
      viewAttachments: 'View attachments',
    },
    statuses: {
      waitingDoctorAssignment:
        'Your request is under review and you will be notified once confirmed. {{urgency}}',
      waitingDoctorConfirmation:
        'Your request has been sent to the doctor and is awaiting confirmation. {{urgency}}',
      confirmed: 'Request confirmed. Due by: {{date}}',
      finished: 'Second opinion completed.',
    },
    timeline: {
      referred: {
        title: 'Case referred to another doctor',
        message:
          'The second opinion request has been transferred to another specialist for review.',
      },
      created: {
        title: 'Second opinion request #{{number}} created',
        message:
          'Your request was created successfully. It will be reviewed and assigned to a specialist as soon as possible.',
      },
      finished: {
        title: 'Second opinion completed',
        message:
          'The case review is complete and the report has been attached.',
      },
      doctorAssigned: {
        title: 'Doctor assigned',
        message: 'Dr. {{name}} has been assigned to review your case.',
      },
      doctorConfirmed: {
        title: 'Request confirmed by doctor',
        message:
          'The doctor acknowledged the request and will start reviewing the case.',
      },
      moreInfoNeeded: {
        title: 'More information needed',
        message:
          'The doctor is requesting more information to better review your case. Please provide the requested information.',
      },
      infoSubmitted: {
        title: 'Additional information submitted',
        message:
          'Additional information has been submitted and is being reviewed by the doctor.',
      },
    },
    symptomChecker: {
      title: 'Add supporting information',
      subtitleSelf:
        'Please fill in your details and attach any files if available',
      subtitleB2B:
        "Please fill in the patient's details and attach any files if available",
      urgentCheckbox: 'Mark as urgent',
      continue: 'Next: confirm request',
      priceSummaryNormal:
        'Standard price: <strong>260 SAR</strong>. Expected response in <strong>10 days</strong>',
      priceSummaryUrgent:
        'Urgent add-on: <strong>200 SAR</strong>. Expected response in <strong>2 days</strong>',
    },
    submitInfo: {
      trigger: 'Submit additional information',
      title: 'Submit additional information',
      doctorRequestedPrefix: 'Information the doctor asked for:',
      details: 'Details',
      detailsPlaceholder:
        'Please write the additional information requested here...',
      submit: 'Send',
      submitting: 'Sending...',
      validation: 'Please enter details or attach at least one file',
    },
    confirmation: {
      title: 'Confirm booking',
      subtitle: 'Please confirm the booking',
      detailsHeader: 'Booking details',
      urgentLabel: 'Urgent request',
      notUrgentLabel: 'Not urgent',
      urgentWithFee: 'Urgent: +{{fee}}. Expected response in two days.',
      notUrgentDescription: 'Not urgent: expected response within 13 days.',
      confirm: 'Confirm booking',
      payAndConfirm: 'Pay and confirm',
      processing: 'Processing...',
      back: 'Back',
      missingDoctorName: 'Please enter the referring doctor name',
    },
  },
  homecare: {
    pageTitle: 'Homecare services',
    vendors: 'Service providers',
    services: 'Services',
    reservationDetails: 'Reservation details',
    selectDate: 'Select a date',
    selectTime: 'Select a time',
    selectService: 'Select a service',
    selectedVendor: 'Selected provider',
    continue: 'Continue',
    back: 'Back',
    requiredAmount: 'Amount due',
    provider: 'Provider',
    appointment: 'Appointment',
    noVendors: 'No providers available',
    noServices: 'No services available',
    confirmation: {
      title: 'Confirm booking',
      subtitle: 'Please confirm the booking',
      detailsHeader: 'Booking details',
      paymentDetailsHeader: 'Payment details',
      confirm: 'Confirm booking',
      payAndConfirm: 'Pay and confirm',
      processing: 'Processing...',
    },
    bookService: {
      title: 'Book your appointment',
      subtitle: 'Choose the service and a suitable time',
      needsSubtitle:
        'The service provider will review this information when your request is received',
      describeNeeds: 'Describe your needs',
      symptomsPlaceholder:
        'I need home nursing to monitor blood pressure and blood sugar...',
      hints: {
        service: 'Which service do you need?',
        chronic: 'Do you have any chronic illnesses?',
        duration: 'How long have you needed this care?',
      },
      selectAppointment: 'Select appointment',
      total: 'Total',
      booking: 'Booking...',
      vendorNotFound: 'Provider not found',
      validation: {
        serviceRequired: 'Please choose a service',
        symptomsMin:
          'Please describe your needs in detail (at least 10 characters)',
        slotRequired: 'Please choose a time slot',
        patientInfoRequired:
          'Please enter the patient information (name, birth date, gender)',
      },
    },
    serviceListing: {
      searchPlaceholder: 'Search for a service or provider...',
      vendorTypeIndividual: 'Provider',
      vendorTypeClinic: 'Clinic',
      cityPlaceholder: 'City',
      allCities: 'All cities',
      dayPlaceholder: 'Day',
      allDays: 'All days',
      noResultsTitle: 'No results',
      noResultsForQuery: 'No results found containing "{{query}}"',
      noResultsWithFilters: 'No services available with the selected filters',
      clearFilters: 'Clear filters',
      bookNow: 'Book now',
      yearsExperience: '{{value}} years of experience',
      availableLabel: 'Available:',
      availableDaily: 'Available daily',
      priceFrom: 'From',
      priceCurrency: 'SAR',
      weekdays: {
        SUNDAY: 'Sunday',
        MONDAY: 'Monday',
        TUESDAY: 'Tuesday',
        WEDNESDAY: 'Wednesday',
        THURSDAY: 'Thursday',
        FRIDAY: 'Friday',
        SATURDAY: 'Saturday',
      },
    },
    reservationDetail: {
      headerTitle: 'Homecare #{{counter}}',
      backAria: 'Back',
      notFound: 'Reservation not found',
      sectionTitle: 'Reservation details',
      service: 'Service',
      date: 'Date',
      time: 'Time',
      price: 'Price',
      descriptionLabel: 'Case description',
      cancelButton: 'Cancel reservation',
      attachmentsTitle: 'Attachments',
      timelineTitle: 'Activity',
      providerTitle: 'Provider',
      providerBio: 'About',
      vendorTypeIndividual: 'Individual',
      vendorTypeClinic: 'Organization',
      patientTitle: 'Patient information',
      patientName: 'Name',
      patientPhone: 'Phone',
      patientEmail: 'Email',
      cancelDialog: {
        title: 'Confirm cancellation',
        description:
          'Are you sure you want to cancel homecare reservation #{{counter}}? This action cannot be undone.',
        cancel: 'Cancel',
        confirm: 'Confirm cancellation',
      },
      timeline: {
        CREATED: 'Reservation created',
        CONFIRMED: 'Reservation confirmed',
        FINISHED: 'Service completed',
        CANCELED: 'Reservation canceled',
      },
      statuses: {
        WAITING_CONFIRMATION: 'Awaiting provider confirmation',
        CONFIRMED: 'Appointment confirmed',
        FINISHED: 'Service completed',
        CANCELED: 'Reservation canceled',
      },
      cancelErrors: {
        missingDate: 'Could not find the appointment date.',
        tooLate: 'Cannot cancel within {{hours}} hours of the appointment.',
      },
      cancelSuccess: 'Reservation canceled successfully',
      cancelError:
        'Could not cancel the reservation. Refresh the page and try again.',
      finishButton: 'Finish service',
      finishNotStartedYet:
        'You can finish the service after the visit start time',
      finishSuccess: 'Service finished successfully',
      finishError:
        'Could not finish the service. Refresh the page and try again.',
      finishDialog: {
        title: 'Confirm service completion',
        description:
          'Did the homecare visit #{{counter}} take place? The reservation will be marked as completed.',
        cancel: 'Back',
        confirm: 'Yes, finish service',
      },
    },
    paymentDescription: 'Homecare service booking - {{service}}',
  },
  specialties: {
    pageTitle: 'All specialties',
    searchPlaceholder: 'Search specialties...',
    searchResultsFor: '{{value}} results for "{{query}}"',
    empty: 'No specialties',
    emptyDescription: 'No specialties available right now',
    emptySearch: 'No specialties matching "{{query}}"',
  },
  profilePage: {
    pageTitle: 'Profile',
    personalInfo: 'Personal information',
    accountInfo: 'Account information',
    logout: 'Log out',
    editInfo: 'Edit information',
    save: 'Save changes',
    cancel: 'Cancel',
    saving: 'Saving...',
    firstName: 'First name',
    parentName: "Father's name (optional)",
    lastName: 'Last name',
    email: 'Email',
    phone: 'Phone',
    phoneNumber: 'Phone number',
    birthDate: 'Date of birth',
    gender: 'Gender',
    ageLabel: 'Age: {{value}}',
    residency: 'Place of residence (optional)',
    residencyLabel: 'Residence: {{value}}',
    genderLabel: 'Gender: {{value}}',
    documentType: 'Document type',
    documentId: 'Document number',
    insuranceCompany: 'Insurance company',
    addInsuranceDocument: 'Add insurance details',
    editInsuranceDocument: 'Edit insurance details',
    accountType: 'Account type',
    organizationAccount: 'Organization account',
    organizationName: 'Organization name',
    createdAt: 'Created on',
    phoneNumberLine: 'Phone: {{value}}',
    emailLine: 'Email: {{value}}',
    updateSuccess: 'Profile updated successfully',
    termsLink: 'Terms of service and privacy',
    termsDescription: 'Review the terms of use and privacy policy',
    contactUsWhatsapp: 'Contact us on WhatsApp',
    contactUsWhatsappDescription: 'Chat directly with support',
  },
  notifications: {
    pageTitle: 'Notifications',
    markAllRead: 'Mark all as read',
    noNotifications: 'No notifications',
    noNotificationsDescription: 'No notifications at this time',
    loadMore: 'Load more',
  },
  medicalRecords: {
    pageTitle: 'Medical records',
    empty: 'No records.',
    date: 'Date',
    doctor: 'Doctor',
    vendor: 'Service provider',
    detail: 'Specialty',
    service: 'Service',
    recordNumber: 'Record #',
    actions: 'Actions',
    view: 'View',
    download: 'Download',
    reports: {
      prescription: 'Download prescription',
      labTests: 'Download medical analysis',
      diagnosis: 'Download diagnosis',
    },
    print: 'Print',
    close: 'Close',
  },
  payments: {
    pageTitle: 'Payment',
    back: 'Back',
    submit: 'Complete payment',
    processing: 'Processing...',
    success: 'Payment completed successfully',
    failed: "Couldn't complete the payment",
    missingReservation: "Couldn't find the requested booking",
    logoAlt: 'Virtual Care Logo',
  },
  meeting: {
    back: 'Back',
    endCall: 'End call',
    waiting: 'Waiting for the doctor to join',
    chat: 'Chat',
    mute: 'Mute',
    unmute: 'Unmute',
    cameraOn: 'Camera on',
    cameraOff: 'Camera off',
  },
  appointments: {
    review: {
      invalidParams: 'Invalid meeting or reservation link',
    },
    reviewSubmitted: {
      headerPrefix: 'Appointment: #{{number}}',
      successTitle: 'Your request was received',
      successDescription:
        'Your request will be reviewed and a doctor will be assigned as soon as possible. You will be notified by WhatsApp or email once the appointment is confirmed.',
    },
    actions: {
      followup: {
        trigger: 'Schedule follow-up',
        validationRequired: 'Please select a date and time',
        success: 'Follow-up appointment scheduled successfully',
      },
      cancel: {
        trigger: 'Cancel appointment',
        checking: 'Checking...',
        success: 'Appointment canceled successfully.',
        refundSuffix: ' {{amount}} {{currency}} will be refunded.',
        checkError: 'Could not check cancellation eligibility.',
        fallback: 'This appointment cannot be canceled.',
        reasons: {
          PAST_DEADLINE:
            'Cannot cancel — the cancellation window has closed (6 hours before the appointment).',
          NO_PAYMENT: 'No payment is associated with this appointment.',
          ALREADY_CANCELED: 'This appointment is already canceled.',
          ALREADY_FINISHED: 'Completed appointments cannot be canceled.',
          NOT_OWNER: 'You do not have permission to cancel this appointment.',
        },
      },
      submitInfo: {
        trigger: 'Submit additional information',
        title: 'Submit additional information',
        doctorRequestedPrefix: 'Information the doctor asked for:',
        details: 'Details',
        detailsPlaceholder:
          'Please write the additional information requested here...',
        submit: 'Send',
        submitting: 'Sending...',
        validation: 'Please enter details or attach at least one file',
      },
    },
    pageTitle: 'My appointments',
    tabs: {
      standard: 'Doctor appointments',
      secondOpinion: 'Second opinion',
      homecare: 'Homecare',
      printing: '3D Printing',
    },
    empty: {
      standard: 'No appointments.',
      secondOpinion: 'No second opinion requests.',
      homecare: 'No homecare reservations.',
      printing: 'No printing requests yet.',
    },
    columns: {
      recordNumber: 'Record #',
      provider: 'Doctor name',
      vendor: 'Service provider',
      detail: 'Specialty',
      service: 'Service',
      appointmentDate: 'Appointment',
      deliveryTime: 'Delivery time',
      createdAt: 'Booked on',
      requestedAt: 'Requested on',
      status: 'Status',
    },
    detail: {
      notFound: 'Appointment not found.',
      canceled: 'Appointment canceled',
      joinNotice:
        'You can join the appointment up to {{minutes}} minutes before the scheduled time',
      copyLink: 'Copy meeting link',
      linkCopied: 'Link copied',
      copyFailed: 'Could not copy link, please copy it manually',
      guestEnded: {
        title: 'Call ended',
        description: 'Thank you. You can close this page now.',
      },
      guestError: {
        invalid: 'Invalid link',
        invalidDescription:
          'This link is invalid or has expired. Please contact the party that sent it.',
        tooEarly: 'Meeting has not started yet',
        tooEarlyDescription:
          'You can join the meeting up to 30 minutes before the scheduled time.',
        ended: 'Meeting has ended',
        endedDescription: 'The meeting has ended and can no longer be joined.',
        notAvailable: 'Meeting not available',
        notAvailableDescription:
          'This meeting is not currently available. Please contact the party that sent the link.',
      },
      refunded: '{{amount}} {{currency}} has been refunded',
      currencySar: 'SAR',
      back: 'Back',
      defaultTitle: 'Appointment',
      card: {
        titleWithSpecialty: 'Appointment with {{specialty}}',
        statusLabel: 'Status:',
        priceLabel: 'Price:',
        paid: 'Paid',
        appointmentNumber: 'Appointment number:',
        appointmentDate: 'Appointment date:',
        serviceType: 'Service type:',
        caseDoctor: 'Assigned doctor:',
        followupAvailable:
          'You can request a follow-up with the doctor within 13 days 20:25 hours',
        attachmentsHeader: 'Submitted information',
        viewAttachments: 'View attachments',
      },
      finished: {
        closedOn: 'Case closed successfully on: {{date}}',
      },
      additional: {
        title: 'Additional information',
        doctorNotes: "Doctor's notes",
        noNotes: 'None',
      },
      patientSummary: {
        name: 'Name',
        age: 'Age',
        gender: 'Gender',
        specialty: 'Selected specialty',
        attachments: 'Attached files',
        noAttachments: 'None',
      },
      patientInfo: {
        title: 'General information',
        name: 'Name:',
        membership: 'Membership ID:',
        phone: 'Phone:',
        email: 'Email:',
        age: 'Age:',
        gender: 'Gender:',
        residence: 'Residence:',
        documentId: 'Document number:',
        entity: 'Organization:',
        cancelNotice: 'You can cancel up to 24 hours after submission.',
        editNotice: 'Editing attached files may delay the appointment.',
        usageLimit: 'Available credit:',
        usageLimitCount: '{{value}} consultations',
      },
      payment: {
        title: 'Payment details',
        total: 'Total amount:',
        insuranceDiscount: 'Insurance discount:',
        finalAmount: 'Amount due:',
      },
      diagnosisCards: {
        sectionTitle: 'Visit results',
        visit: 'Visit #{{number}}',
        noResults: 'No results for this visit.',
        medications: 'Medications',
        labTests: 'Lab tests',
        icd10: 'Medical diagnosis',
        downloadReport: 'Download medical report',
        downloadPrescription: 'Download prescription',
        downloadLabTests: 'Download lab tests',
        downloadSickLeave: 'Download sick leave certificate',
        prescriptionDetails:
          '{{strength}} {{unit}} | {{dosage}}x/day | {{period}} | {{form}}',
      },
      attachmentsDialog: {
        title: 'Attached information',
        selectedSpecialty: 'Selected specialty',
        attachedFiles: 'Attached files',
        uploadNewFile: 'Upload new file',
        close: 'Close',
        viewFile: 'View',
      },
      appointmentResults: {
        title: 'Appointment results',
        farewell: 'Wishing you a speedy recovery.',
        downloadPrescription: 'Download prescription',
        downloadLabTests: 'Download lab tests',
        downloadReport: 'Download medical report',
        downloadSickLeave: 'Download sick leave certificate',
        medications: 'Medications',
        labTests: 'Lab tests',
        icd10: 'Medical diagnosis',
        prescriptionDetails:
          'Strength: {{strength}} {{unit}} | Dose: {{dosage}}x/day | Period: {{period}} | Form: {{form}}',
      },
      statuses: {
        waitingDoctorAssignment:
          'Your request is under review. You will be notified once confirmed.',
        waitingDoctorConfirmation:
          'Your request has been sent to the doctor and is awaiting confirmation.',
        confirmed: 'Confirmed appointment time: {{date}} - {{time}}',
        finished: 'Appointment completed on: {{date}} - {{time}}',
        canceledOn: 'Appointment canceled on: {{date}}',
        expired: 'This appointment has expired.',
      },
      timeline: {
        header: 'Activity',
        reservationCreated: {
          title: 'Appointment #{{number}} created',
          message:
            'New booking created successfully. Your request will be reviewed and a specialist doctor will be assigned as soon as possible.',
        },
        reservationCanceled: {
          title: 'Cancellation confirmed',
          message:
            'Your appointment has been canceled successfully at your request.',
        },
        reservationFinished: {
          title: 'Status updated to completed',
          message:
            'Status was updated to completed after the allowed review period expired.',
        },
        doctorAssigned: {
          title: 'Doctor assigned',
          message: 'Dr. {{name}} has been assigned to follow up on your case.',
        },
        appointmentConfirmed: {
          title: 'Appointment confirmed',
          message: 'Appointment confirmed on: {{date}} - {{time}}',
        },
        waitingDoctorConfirmation: {
          title: 'Awaiting doctor confirmation',
          message:
            'Your appointment request has been sent to the doctor and is awaiting confirmation.',
        },
        infoSubmitted: {
          title: 'Additional information submitted',
          message:
            'The patient submitted additional information or attachments. Please review the updated details.',
        },
        moreInfoNeeded: {
          title: 'More information needed',
          message:
            'The doctor is requesting more information to better review your case. Please provide the requested information.',
        },
        patientJoinedCall: {
          title: 'Patient joined the call',
          message: 'Your arrival to the call has been recorded.',
        },
        doctorJoinedCall: {
          title: 'Doctor joined the call',
          message: 'The doctor joined the call.',
        },
        referred: {
          title: 'Referred to another doctor',
          message:
            'You have been referred to another doctor for further evaluation. Appointment details will be updated soon.',
        },
        markedAvailableForFollowup: {
          title: 'Marked available for follow-up',
          message:
            'The case is available for follow-up. You can book a follow-up within the available window.',
        },
        followupWindowExpired: {
          title: 'Follow-up window expired',
          message: 'The follow-up window for this appointment has expired.',
        },
        followupTimeRejected: {
          title: 'Follow-up time rejected',
          message:
            'The doctor rejected the selected follow-up time. Please choose another.',
        },
      },
    },
  },
  shell: {
    language: {
      switchTo: 'Switch to',
      arabic: 'العربية',
      english: 'English',
      aria: 'Change language',
    },
    sidebar: {
      home: 'Home',
      appointments: 'Appointments',
      doctors: 'Doctors',
      labResults: 'Lab Results',
      copilot: 'Copilot',
      profile: 'Profile',
      notifications: 'Notifications',
      logout: 'Log out',
    },
  },
  auth: {
    common: {
      signin: 'Sign in',
      signup: 'Create an account',
      signingIn: 'Signing in...',
      processing: 'Processing...',
      continue: 'Continue',
      back: 'Back',
      phone: 'Phone',
      email: 'Email',
      password: 'Password',
      newPassword: 'New password',
      confirmPassword: 'Confirm password',
      emailPlaceholder: 'me@example.com',
      returnToLogin: 'Back to sign in',
    },
    signin: {
      title: 'Sign in',
      subtitle: 'Enter your details below.',
      noAccount: "Don't have an account?",
      createAccount: 'Create one',
      b2bLogin: 'Sign in with a hospital account',
    },
    signup: {
      title: 'Create an account',
      subtitle: 'Join now for more confident health decisions.',
      agreeTo: 'I agree to the',
      termsLinkLabel: 'terms of use and privacy policy',
      haveAccount: 'Already have an account?',
      submit: 'Create account',
    },
    verifyPhone: {
      title: 'Verify your phone',
      subtitle: 'Enter the verification code sent to your phone number',
      didntReceive: "Didn't receive it?",
      verifying: 'Verifying...',
      submit: 'Verify phone',
      resend: 'Resend',
      attemptCount: '(Attempt {{value}})',
      expired: 'Expired',
    },
    b2bLogin: {
      title: 'Sign in',
      subtitle: 'Enter your details below.',
      forgotPassword: 'Forgot your password?',
    },
    b2bForgotPassword: {
      title: 'Forgot your password?',
      subtitle: 'Enter your email below and we will send you a reset link.',
      submit: 'Send link',
      success: 'A password reset link has been sent to your email',
      haveAccount: 'Have an account?',
    },
    b2bResetPassword: {
      title: 'Reset password',
      subtitle: 'Enter a new password for the account',
      passwordHint: 'Must be at least 8 characters',
      submit: 'Change password',
      success:
        'Password changed successfully, redirecting to the sign in page...',
      invalidLinkFallback: 'Verification code is invalid or has expired',
      backToForgot: 'Back to password recovery',
      errors: {
        linkNotFound: 'Password reset link not found',
        codeMissing: 'Verification code missing from the link',
        invalidOrExpired: 'Verification code is invalid or has expired',
        genericReset: 'Something went wrong while changing the password',
        mismatch: 'Passwords do not match',
      },
    },
    changePassword: {
      title: 'Change password',
      subtitle: 'For security, set a new password before continuing.',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm password',
      hint: 'Must be at least 8 characters',
      submit: 'Save',
      reSignInRequired: 'Password updated. Please sign in again.',
      validation: {
        currentRequired: 'Current password is required',
        passwordRequired:
          'Password is required and must be at least 8 characters',
        confirmRequired: 'Password confirmation is required',
        mismatch: 'Passwords do not match',
        wrongCurrent: 'Current password is incorrect',
        weakPassword: 'Password is too weak',
        genericError: 'Something went wrong while changing the password',
        syncError: "Couldn't complete the update. Please try again.",
      },
    },
    completeProfile: {
      title: 'Create account',
      subtitle: 'Please fill in your user details below',
      firstName: 'First name',
      parentName: "Father's name (optional)",
      lastName: 'Last name',
      termsAcknowledgement:
        'I confirm the accuracy of the information provided and take responsibility for any inaccurate data.',
    },
    insurance: {
      title: 'Add health insurance',
      subtitle:
        'Add the identity document we will use to look up insurance eligibility. You can skip this step for now.',
      documentType: 'Document type',
      selectDocumentType: 'Select a document type',
      documentId: 'Document number',
      documentTypeRequired: 'Document type is required',
      documentIdRequired: 'Document number is required',
      documentTypes: {
        national: 'Saudi national ID number',
        iqama: 'Iqama number',
        passport: 'Passport number',
      },
      helper:
        'No document upload is required. You can add this information later from your profile.',
      save: 'Save and continue',
      skip: 'Skip for now',
    },
    logout: {
      title: 'Log out',
      confirm: 'Are you sure you want to log out of your account?',
      submit: 'Log out',
      cancel: 'Cancel',
    },
  },
  shared: {
    firebase: {
      invalidCredentials: 'Email or password is incorrect',
      tooManyAttempts:
        'Sign in is temporarily blocked due to too many attempts. Please try again later',
      accountDisabled: 'This account has been disabled. Please contact support',
      networkFailed: 'Network connection failed. Please check your internet',
      loginNotSupported:
        'Email and password sign-in is not currently supported',
      invalidEmail: 'Email is invalid. Please try again',
      invalidPassword: 'Password is incorrect. Please try again',
      loginError: 'Something went wrong while signing in',
      logoutError: 'Something went wrong while signing out',
      invalidPhone: 'Phone number is invalid. Please try again',
      emailInUse: 'This email is already in use',
      phoneInUse: 'This phone number is linked to another account',
      accountExistsDifferentMethod:
        'An account already exists with this email using a different sign-in method. Please contact support',
      recaptchaError: 'Verification error. Please try again',
      signupError: 'Something went wrong while signing in',
      confirmationNotFound:
        'No active verification found. Please resend the code',
      invalidCode: 'Verification code is incorrect. Please try again',
      phoneVerificationError: 'Something went wrong verifying the phone',
      noActiveVerification: 'No active verification found. Please try again',
      emailNotRegistered: 'Email is not registered',
      resetEmailError: 'Something went wrong sending the reset link',
      resetCodeError: 'Something went wrong sending the verification code',
    },
    api: {
      notB2bPatient: 'This service is only available to corporate patients',
      b2bPatientProfileNotFound:
        'Patient profile not found. Please contact the administration',
      b2bUsageLimitExhausted:
        'You have used up the available credit. Please contact the administration',
      filesNotFound: 'Some of the attached files are invalid',
      noDoctorsAvailable: 'No doctors available at the selected time',
      doctorNotAvailable: 'Doctor is not available at the selected time',
      recordNotFound: 'The requested data was not found',
      validationFailed: 'Submitted data is invalid',
      forbidden: 'You are not allowed to access this',
      uniqueConstraint: 'The value entered in {{fields}} is already in use',
      validationGeneric: 'Data validation error',
      formValidation: 'Form validation error',
      genericUnexpected: 'Something unexpected went wrong',
      retryLater: 'Something unexpected went wrong. Please try again later',
      unauthorized: 'You are not authorized to perform this action',
      featureDisabled: 'This feature is currently unavailable',
    },
    fields: {
      phoneNumber: 'Phone number',
      email: 'Email',
      fallback: 'field',
    },
    validation: {
      phoneRequired: 'Phone number is required',
      phoneRequiredAlt: 'Phone number is required',
      termsRequired: 'You must accept the terms and conditions',
      otpLength: 'Enter the 6-digit verification code',
      nameRequired: 'Name is required',
      nameCharactersOnly:
        'Name must contain letters only (no numbers or special characters)',
      emailInvalid: 'Email is invalid',
      emailRequired: 'Email is required',
      passwordRequired: 'Password is required',
      passwordMin: 'Password must be at least 8 characters',
      confirmPasswordRequired: 'Password confirmation is required',
      birthDateRequired: 'Date of birth is required',
      genderRequired: 'Gender is required',
      termsAcknowledgeRequired:
        'You must confirm that the information is accurate',
      fileMimeNotSupported:
        'Unsupported file format. Allowed formats: PDF, PNG, JPG, ZIP',
      fileSizeExceeded:
        'File size exceeds the limit (10 MB, 2 GB for ZIP files)',
    },
  },
  printing: {
    title: '3D Printing requests',
    subtitle:
      'Send a scan or model and our specialist team will produce it with our partner hospital.',
    submitted: 'Your printing request has been submitted',
    submittedWithRemaining:
      'Request submitted. Remaining prints: {{remaining}}.',
    topUpHint: 'Contact your administrator to top up.',
    quotaRemaining: 'Remaining quota',
    form: {
      description: 'Request description',
      descriptionPlaceholder:
        'e.g. Please print a mandible model for surgical planning...',
      files: 'Attached files (ZIP)',
      submit: 'Submit request',
      submitting: 'Submitting...',
    },
    cards: {
      prefix: 'Printing request',
      doctorName: 'Assigned doctor',
    },
    detail: {
      linkReady: 'Your download link is ready',
      downloadUrl: 'Download URL',
      downloadPassword: 'Password',
      openLink: 'Open link in new tab',
      requestDetails: 'Request details',
      description: 'Description',
      submittedOn: 'Submitted on',
      files: 'Files',
      downloadFile: 'Download',
    },
    validation: {
      descriptionRequired: 'Please describe what you would like printed',
      filesRequired: 'Please attach at least one file (ZIP)',
    },
  },
};

export default en;
